/**
 * Mobile Filters CTA — opens DirectoryFilterSheet.
 * Active visual when sort/org facets differ from defaults (not search query alone).
 */

import { Button } from './Button.js';
import { CUSTOMER_MIN_TOUCH_TARGET_CLASS } from './customerTouchTarget.js';

export interface DirectoryFilterButtonProps {
  readonly active: boolean;
  readonly expanded: boolean;
  readonly onClick: () => void;
  readonly label: string;
  readonly testIdPrefix: string;
  readonly className?: string;
}

function FiltersIcon({ className }: { readonly className?: string }): JSX.Element {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      aria-hidden="true"
      focusable="false"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    >
      <path d="M4 7h16" />
      <path d="M7 12h10" />
      <path d="M10 17h4" />
    </svg>
  );
}

export function DirectoryFilterButton(props: DirectoryFilterButtonProps): JSX.Element {
  const { active, expanded, onClick, label, testIdPrefix, className } = props;

  return (
    <Button
      intent="secondary"
      size="md"
      type="button"
      aria-expanded={expanded}
      aria-haspopup="dialog"
      data-testid={`${testIdPrefix}-open-sheet`}
      onClick={onClick}
      className={[
        'relative inline-flex shrink-0 items-center justify-center gap-1.5',
        CUSTOMER_MIN_TOUCH_TARGET_CLASS,
        active
          ? 'border-[var(--color-action-primary)] ring-1 ring-[var(--color-action-primary)]'
          : '',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <FiltersIcon className="h-4 w-4 shrink-0" />
      <span className="whitespace-nowrap">{label}</span>
      {active ? (
        <span
          className="absolute -right-1 -top-1 h-2.5 w-2.5 rounded-full bg-[var(--color-action-primary)]"
          aria-hidden="true"
          data-testid={`${testIdPrefix}-filters-active-badge`}
        />
      ) : null}
    </Button>
  );
}
